import { ImageName } from "@mcleod/images";
import { CommonTooltips } from "@mcleod/common";
import { Button, ClickEvent, DataDisplayEvent, DataSourceAction, DataSourceExecutionEvent, Image, ImageType, Label, Layout, LookupModelSearchEvent, Snackbar, Textbox } from "@mcleod/components";
import { ActionPermission, Alignment, Api, CityUtil, CurrencySettings, DateFormat, DateUtil, DisplayType, ExtendedDateFormat, HorizontalAlignment, LogManager, Model, ModelRow, PermissionsUtil, StringUtil } from "@mcleod/core";
import { getDispatchControlBoolean, LMECompanySettings, LMEUserSettings } from "@mcleod/general";
import { ModifyCarrierPay } from "@mcleod/powerbroker";
import { SmsUtility } from "@mcleod/powerbroker/src/SmsUtility";
import { AssignedMovementActions } from "./AssignedMovementActions";
import { AutogenLayoutAssignedMovementOverview } from "./autogen/AutogenLayoutAssignedMovementOverview";
import { CLEARED_STATUS_CODE, Movement } from "./Movement";
import { PayAdvance } from "./PayAdvance";

const log = LogManager.getLogger("dispatch.AssignedMovementOverview");
const BTM_STATUS = ["C", "P", "D"];

export class AssignedMovementOverview extends AutogenLayoutAssignedMovementOverview {

    movementActions: AssignedMovementActions;
    trackingLogo: Image;
    driverOneOptedOut: boolean;
    driverTwoOptedOut: boolean;

    override async onLoad() {
        if (this._designer == null)
            await this.addAssignedMovementActionsLayout();
        this.panelMoveInfo.visible = this.mainDataSource?.owner instanceof Movement;

        this.panelDataFusion.visible = false;
        this.layoutDriverInformation.afterDriverSelection = (selectedRow: ModelRow, driverNumberSuffix: string) => {
            this.labelOverrideDriver2Name.visible = this.sourceMovement.activeRow.get("override_driver2_nm") != null;
            this.buttonSendSmsDriver2.visible = this.sourceMovement.activeRow.get("override_driver2_nm") != null;
            this.labelOverrideDriverName.visible = this.sourceMovement.activeRow.get("override_driver_nm") != null;
            this.buttonSendSmsDriver.visible = this.sourceMovement.activeRow.get("override_driver_nm") != null;
            this.labelTeam.visible = this.sourceMovement.activeRow.get("override_driver2_nm") != null;
            this.setSmsButtonImage(selectedRow, this.buttonSendSmsDriver2, driverNumberSuffix);
        }
        this.layoutDriverInformation.replaceMainDatasource(this.sourceMovement);
    }

    addAssignedMovementActionsLayout(): Promise<AssignedMovementActions> {
        return new Promise(resolve => {
            Layout.getLayout("lme/dispatch/AssignedMovementActions").addLayoutLoadListener(event => {
                this.movementActions = event.target as AssignedMovementActions;
                this.movementActions.replaceMainDatasource(this.sourceMovement);
                this.tabsetCarrierDriver.addCenterTool(this.movementActions);
                resolve(this.movementActions);
            });
        })
    }

    sourceMovementAfterExecution(event: DataSourceExecutionEvent) {
        if (event?.getAction() == DataSourceAction.SEARCH || event?.getAction() == DataSourceAction.UPDATE) {
            if (this.activeRow == null) return;
            const orderId = this.sourceMovement.activeRow?.get("orders.id");
            this.labelOverrideDriver2Name.visible = !this.activeRow?.isNull("override_driver2_nm");
            this.buttonSendSmsDriver2.visible = !this.activeRow?.isNull("override_driver2_nm");
            const isSmsLicensed = this.sourceMovement.activeRow?.get("is_sms_licensed_and_active");
            if (orderId != null) {
                this.sourceEquipMatchDetail.search({
                    _parent_link: {
                        model: this.sourceMovement.url,
                        "orders.id": orderId
                    }
                }).then(() => {
                    this.panelEquipmentRequired.visible = this.sourceEquipMatchDetail.data.length > 0;
                });
            }
            this.resetTrackingPanel();
            this.panelTempRange.visible = this.sourceMovement.activeRow?.get("orders.temperature_min") != null;
            this.tabTrackingRequirements.visible = this.sourceMovement.activeRow?.get("override_payee_id") != null;
            this.setTrackingBadgeProps(this.sourceMovement.activeRow);

            this.addOrderMovementReferenceNumberTextboxes();
            this.setCarrierRateData();
            if (isSmsLicensed) {
                this.setSmsButtonDetails(this.layoutDriverInformation.textboxOverrideDriver1Nm, this.layoutDriverInformation.textboxOverrideDriver2Nm, this.sourceMovement.activeRow?.get("override_payee_id"));
            }
            this.setCarrierEmptyCityState();
            this.labelTeam.visible = this.sourceMovement.activeRow?.get("override_driver2_nm") != null;
            this.addTrackingLogo();
            this.cityStateCarrierEmptyFromCityIdPickupDist();
            PayAdvance.validateMovement(this);
            this.movementActions.setMenuOptions(this.sourceMovement.activeRow?.get("override_payee_id"));
            this.setCarrierStarRatingVisible(this.sourceMovement.activeRow);
            this.setupBuyToMarket(this.sourceMovement.activeRow);
            this.setupModifyPayButton();
            if (!this.sourceMovement.activeRow?.isNull("xfer2settle_date")) {
                this.imageTransferToSettlement.visible = true;
                this.imageTransferToSettlement.tooltip = "Transferred to Settlement on " + DateUtil.formatDateTime(this.sourceMovement.activeRow?.getDateTime("xfer2settle_date"));
            }
            this.configurePanelLoadIcons();
        }
        this.setCommissionableMarginTooltip();
    }

    configurePanelLoadIcons() {
        this.panelLoadIcons.visible = this.sourceMovement.activeRow?.get("brokerage", "N") === "N";
        if (!this.sourceMovement.activeRow?.isNull("xfer2settle_date")) {
            this.imageTransferToSettlement.visible = true;
            this.imageTransferToSettlement.tooltip = "Transferred to Settlement on " + DateUtil.formatDateTime(this.sourceMovement.activeRow?.getDateTime("xfer2settle_date"));
        }
    }

    setupModifyPayButton() {
        const isDelivered = this.sourceMovement.activeRow?.get("status", null) === "D";
        if (isDelivered && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_MOVES)) {
            this.buttonModifyPay.enabled = false;
            this.buttonModifyPay.disabledTooltip = "Carrier Pay cannot be updated after delivery.";
        }

        const isTransferred: boolean = !this.sourceMovement.activeRow.isNull("xfer2settle_date");
        const isBilled: boolean = !this.sourceMovement.activeRow.isNull("orders.bill_date");
        if ((isTransferred || isBilled)
            && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_MOVE_AFTER_SETTLED)) {
            this.buttonModifyPay.enabled = false;
            this.buttonModifyPay.disabledTooltip = "Carrier Pay cannot be updated after transferred to settlement.";
        }
    }

    setupBuyToMarket(move: ModelRow) {
        const brokerage = move.getBoolean('brokerage');
        const status = move.getString("status");
        const btmExclude = move.getBoolean('buy_to_market_ex');
        const showBtm = getDispatchControlBoolean("show_btm", false);

        this.panelBuyToMarket.visible = showBtm && brokerage && BTM_STATUS.includes(status);

        const showBtmFields = this.panelBuyToMarket.visible && !btmExclude;
        this.textboxBuyToMarket.visible = showBtmFields;
        this.textboxBuyToMarketPercent.visible = showBtmFields;

        if (this.textboxBuyToMarket.visible && !move.isNull("buy_to_market")) {
            this.textboxBuyToMarket.printableLabel.color =
                this.textboxBuyToMarketPercent.color = this.sourceMovement.activeRow.isNull("buy_to_market")
                    ? "default"
                    : this.sourceMovement.activeRow.get("buy_to_market")?.includes("OVER")
                        ? "error"
                        : "success";
        }

    }

    async sourceMovementOnDisplay() {
        const movementId = this.mainDataSource.activeRow?.get("id") as string;
        const orderId = this.mainDataSource.activeRow?.get("orders.id") as string;
        const customerId = this.mainDataSource.activeRow?.get("customer.id") as string;
        const pbLtl = this.mainDataSource.activeRow?.get("is_brokerage_ltl");

        const layoutMovementDataFusionSummary = this.layoutMovementDataFusionSummary;
        if (movementId && orderId && customerId) {
            layoutMovementDataFusionSummary.movementId = movementId;
            layoutMovementDataFusionSummary.orderId = orderId;
            layoutMovementDataFusionSummary.customerId = customerId;
            const filter = [
                {
                    movement_id: movementId,
                    order_id: orderId,
                    customer_id: customerId,
                    is_brokerage_ltl: pbLtl
                }
            ];
            const body = JSON.stringify(filter);
            await this.delay(1000);
            Api.search("lme/dispatch/movement-datafusion-summary", body).then(response => {
                if (response) {
                    layoutMovementDataFusionSummary.setEdiData(response.data[0]);
                    this.panelDataFusion.visible = layoutMovementDataFusionSummary.displayingData;
                }
            });
        }
    }

    async delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // This Method is use to check if Daily Advance is not "N" or "Null" or "Empty" in User Screen under Settelement Tab
    private hasDailyAdvanceLimits(): boolean {
        const dailyAdvanceLimits = LMEUserSettings.get().daily_advance_limits;
        return !(dailyAdvanceLimits === "N" || dailyAdvanceLimits === null || dailyAdvanceLimits === "");
    }

    resetTrackingPanel() {
        this.panelTracking.visible = false;
        this.labelTrackingRequired.visible = false;
        this.labelNextScheduledHeader.visible = true;
        this.labelNextSchedCall.visible = true;
        this.labelTrackingCaption.visible = false;
        this.panelTracking.remove(this.trackingLogo);
    }

    setSmsButtonDetails(textboxOverrideDriverNm: Textbox, textboxOverrideDriver2Nm: Textbox, overridePayeeId: any) {
        if (!StringUtil.isEmptyString(overridePayeeId) && !StringUtil.equalsIgnoreCase("Undefined", overridePayeeId)) {
            Api.post("lme/powerbroker/obtain-driver-sms-details",
                {
                    "override_payee_id": overridePayeeId,
                    "override_driver_name": textboxOverrideDriverNm.text,
                    "override_driver_name_two": textboxOverrideDriver2Nm.text
                }).then(response => {
                if (!StringUtil.isEmptyString(textboxOverrideDriverNm.text)) {
                    if (response?.data[0]?.override_driver_sms_is_active) {
                        this.buttonSendSmsDriver.visible = true;
                        this.buttonSendSmsDriver.imageName = ImageName.driverMsgNormal;
                        this.driverOneOptedOut = false;
                    } else {
                        this.buttonSendSmsDriver.imageName = ImageName.driverSmsOptedOut;
                        this.buttonSendSmsDriver.tooltip = "Driver Opted Out";
                        this.driverOneOptedOut = true;
                    }
                } else {
                    this.buttonSendSmsDriver2.visible = false;
                }
                if (!StringUtil.isEmptyString(textboxOverrideDriver2Nm.text)) {
                    if (response?.data[0]?.override_driver_two_sms_is_active) {
                        this.buttonSendSmsDriver2.imageName = ImageName.driverMsgNormal;
                        this.driverTwoOptedOut = false;
                    } else {
                        this.buttonSendSmsDriver2.imageName = ImageName.driverSmsOptedOut;
                        this.buttonSendSmsDriver2.tooltip = "Driver Opted Out";
                        this.driverTwoOptedOut = true;
                    }
                } else {
                    this.buttonSendSmsDriver2.visible = false;
                }
            });
        }
    }

    async addTrackingLogo() {
        this.labelTrackingCaption.visible = false;
        const p44Record = await Model.searchSingleRecord("lme/powerbroker/contract-management-control", { id: "P44" });
        if (LMECompanySettings.getSingleton().isBrokerageLtl() && p44Record?.getBoolean("is_active")) {
            const movementId: string = this.sourceMovement.activeRow?.get("id");
            Api.post("lme/powerbroker/p44-ltl-tracking", {
                "action": "check_brltl",
                "movement_id": movementId
            }).then(response => {
                if (response?.data[0]?.is_brokerage_ltl && response?.data[0]?.is_p44_consignment) {
                    response.data[0].movementId = movementId;
                    this.addP44TrackingLogo(response.data[0]);
                } else {
                    this.addTruckloadTrackingLogo();
                }
            }).catch(error => {
                this.handleP44LTLTrackingError(error);
            });
        } else {
            this.addTruckloadTrackingLogo();
        }
    }

    textboxCompanyTrailerBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = 'A';
    }

    addP44TrackingLogo(data: any) {
        if (!data.is_dispatched)
            return;

        if (data.can_lookup_msg) {
            this.setTrackingLogo({ ...data, click_event: this.initP44TrackingClickEvent.bind(this) });
            this.showTrackingPanel(data);
            return;
        }

        if (data.on_click_url) {
            this.setTrackingLogo(data);
            this.showTrackingPanel(data);
        }
    }

    initP44TrackingClickEvent(image: Image, movementId: string) {
        image.busy = true;
        Api.post("lme/powerbroker/p44-ltl-tracking", {
            "action": "init_p44_tracking",
            "movement_id": movementId
        }).then(response => {
            this.setTrackingLogo(response?.data?.[0]);
        }).catch(error => {
            this.handleP44LTLTrackingError(error)
        }).finally(() => image.busy = false);
    }

    handleP44LTLTrackingError(error: any) {
        log.error(error);
        Snackbar.showWarningSnackbar("Tracking Request error", { millisUntilDismissal: 5000 });
    }

    addTruckloadTrackingLogo() {
        const trackingVendor = this.sourceMovement.activeRow?.get("br_vendor");
        if (trackingVendor != null) {
           /* this.labelNextSchedCall.visible = false;
            this.labelNextScheduledHeader.visible = false;*/
            Api.search("lme/powerbroker/tracking/brokerage-tracking-logo",
                {
                    "vendor_id": trackingVendor,
                    "movement_id": this.sourceMovement.activeRow?.get("id"),
                    "order_id": this.sourceMovement.activeRow?.get("orders.id")
                }).then(response => {
                this.setTrackingLogo(response?.data?.[0]);
                this.showTrackingPanel(response?.data?.[0]);
            });
        }
    }

    showTrackingPanel(response: any) {
        const svgContent = response?.svg_content;
        if (this.trackingLogo != null) {
            this.labelTrackingCaption.visible = true;
            this.panelTracking.visible = true;
            this.panelTracking.add(this.trackingLogo);
            if (!StringUtil.isEmptyString(svgContent)) {
                this.trackingLogo.paddingLeft = 10;
            }
        }
    }

    setTrackingLogo(response: any) {
        this.trackingLogo = null;
        const logoBytes = response?.logo_bytes;
        const logoURL = response?.logo_url;
        const svgContent = response?.svg_content;
        const onClickUrl = response?.on_click_url;
        const tooltip = response?.tracking_status || null;

        const isDfm = this.sourceMovement.activeRow?.getBoolean("is_dfm_tracking", false);

        if (!StringUtil.isEmptyString(logoBytes) || !StringUtil.isEmptyString(logoURL) || !StringUtil.isEmptyString(svgContent) || isDfm) {
            if (isDfm && !StringUtil.isEmptyString(logoBytes)) {
                this.trackingLogo = new Image({
                    id: "trackingLogo", imageType: ImageType.IMG, marginLeft: 10,
                    preserveAspectRatio: true, height: 50, width: 150, imageBytes: logoBytes, tooltip: tooltip
                });
            }
            else if (isDfm) {
                this.trackingLogo = new Image({
                    id: "trackingLogo", name: ImageName.trackingPlaceholder, marginLeft: 10,
                    tooltip: tooltip, height: 50, width: 50
                });
            }
            else {
                this.trackingLogo = new Image({
                    id: "trackingLogo", imageType: ImageType.IMG, marginLeft: 6, marginTop: -8,
                    preserveAspectRatio: true, height: 70, width: 120
                });

                if (!StringUtil.isEmptyString(logoBytes)) {
                    this.trackingLogo.imageBytes = logoBytes;
                    this.trackingLogo.tooltip = tooltip;
                } else if (!StringUtil.isEmptyString(logoURL)) {
                    this.trackingLogo._element.setAttribute("src", logoURL);
                    this.trackingLogo.tooltip = tooltip;
                } else if (!StringUtil.isEmptyString(svgContent)) {
                    // Create a data URL for the SVG content using TextEncoder and Buffer
                    const svgBuffer = new TextEncoder().encode(svgContent);
                    const svgBase64 = window.btoa(String.fromCharCode(...svgBuffer));
                    const svgDataUrl = `data:image/svg+xml;base64,${svgBase64}`;
                    this.trackingLogo._element.setAttribute("src", svgDataUrl);
                    this.trackingLogo.tooltip = response?.tracking_status;
                }
            }

            if (!StringUtil.isEmptyString(onClickUrl)) {
                this.trackingLogo.addClickListener(() => window.open(onClickUrl, '_blank', 'noreferrer'));
            } else if (response.click_event) {
                this.trackingLogo.addClickListener(() => response.click_event(this.trackingLogo,
                    this.sourceMovement.activeRow?.get("id")));
            } else {
                this.trackingLogo.addClickListener(() => Snackbar.showSnackbar("Tracking details not found", { millisUntilDismissal: 2000 }));
            }
        }
    }


    setTrackingBadgeProps(modelRow: ModelRow) {
        const showTrackingBadge = modelRow?.get("show_tracking_badge", false);
        this.panelTracking.visible = showTrackingBadge;
        this.labelTrackingRequired.visible = showTrackingBadge;
        this.setTrackingRequiredTooltip(this.mainDataSource.activeRow, modelRow);
        this.labelNextScheduledHeader.visible = !showTrackingBadge;
        this.labelNextSchedCall.visible = !showTrackingBadge;

        this.labelTrackingRequired.text = modelRow?.get("tracking_badge_text", "");
        this.labelTrackingRequired.backgroundColor = modelRow?.get("tracking_badge_color");
    }

    public setStops(stops: ModelRow[]): void {
        this.addStopToHeader(stops);
        if (this.sourceMovement?.activeRow?.get("status") == "D") {
            this.labelNextStopCity.text = "Delivered";
        }
    }

    addStopToHeader(stops: ModelRow[]) {
        let nextStopFound = false;

        stops?.forEach((stop, index) => {
            const cleared = stop.get("status") == CLEARED_STATUS_CODE;

            if (!cleared && !nextStopFound) {
                nextStopFound = true;
                this.addNextStopToHeader(stop);
                this.addOtherReferenceNumberTextboxes(stop);
            }
        });

        this.panelNextStopType.visible = nextStopFound;
    }

    addNextStopToHeader(stop: ModelRow) {
        const nextStopLocation = stop.get("city_name", "") + ", " + stop.get("state", "") + " " + stop.get("zip_code", "");
        const isPickup = stop.get("stop_type") === "PU";
        this.imageNextStopType.name = isPickup ? ImageName.pickup : ImageName.delivery;
        this.labelNextStopCity.text = nextStopLocation;
        this.labelNextStopLocation.text = stop.get("location_name", "");
        this.labelNextStopAddress.text = stop.get("address", "");
        this.labelNextStopAddress2.text = stop.get("address2", "");
        this.labelNextStopScheduled.text = DateUtil.formatDateTime(DateUtil.parseDateTime(stop.get("sched_arrive_early")), DateUtil.getDateFormat(DateFormat.DATE_TIME, ExtendedDateFormat.LONG));
        const nextStopCityId: string = stop.get("city_id");
        Api.search("lme/dispatch/callin", {
            "_max_search_results": 1,
            "movement_id": this.sourceMovement.activeRow.get("id"),
            "newest_first": true
        }).then((rowCallin: ModelRow) => {
            const callinData = rowCallin?.data?.[0];
            if (callinData != null && callinData.next_stop_city_id === nextStopCityId) {
                this.labelNextStopEta.text = (callinData && callinData.new_eta) ? DateUtil.formatDateTime(DateUtil.parseDateTime(callinData.new_eta), DateUtil.getDateFormat(DateFormat.DATE_TIME, ExtendedDateFormat.LONG)) : "--";
                this.labelNextStopDistance.text = (callinData && callinData.miles2consignee) ? callinData.miles2consignee + " " + callinData.miles2consignee_um : "--";
            } else {
                this.labelNextStopEta.text = "";
                this.labelNextStopDistance.text = "";
            }
        });

    }

    addReferenceNumberTextbox = function (caption: string, text: string) {
        if (text != null)
            this.panelRefNbrs.add(new Textbox({
                fillRow: true,
                caption: caption,
                text: text,
                printable: true,
                fontBold: true
            }));
    }

    addOrderMovementReferenceNumberTextboxes() {
        this.panelRefNbrs.removeAll();
        const row = this.sourceMovement.activeRow;
        this.addReferenceNumberTextbox("BOL Number", row?.get("orders.blnum"));
        this.addReferenceNumberTextbox("Consignee Reference Number", row?.get("orders.consignee_refno"));
        this.addReferenceNumberTextbox("Carrier Pro Number", row?.get("pro_nbr"));
    }

    addOtherReferenceNumberTextboxes(stop: ModelRow) {
        const firstStopId = stop.get("id");
        if (firstStopId) {
            Api.search("lme/general/reference-number", { stop_id: firstStopId }).then(response => {
                for (const data of response.data)
                    this.addReferenceNumberTextbox(data.description, data.reference_number);
            });
        }
    }

    setCarrierRateData() {
        const movementId = this.sourceMovement.activeRow?.get("id");
        const payMethod = this.sourceMovement.activeRow?.get("override_type");
        const ratePerUnit = this.sourceMovement.activeRow?.get("override_pay_rate");

        const currencySettings = CurrencySettings.getSingleton();
        let moveCurrency;
        let orderCurrency;
        if (currencySettings.multiCurrencyEnabled()) {
            moveCurrency = this.sourceMovement.activeRow?.get("override_pay_amt_c") ?? null;
            orderCurrency = this.sourceMovement.activeRow?.get("orders.freight_charge_c") ?? null;
        }

        if (movementId) {
           this.sourceCalculateOverridePay.search(
                {
                    "movement_id": movementId,
                    "order_id": this.mainDataSource.activeRow?.get("orders.id"),
                    "pay_method": payMethod,
                    "rate_per_unit": ratePerUnit,
                    "move_currency_code": moveCurrency,
                    "order_currency_code": orderCurrency,
                    "target_pay": this.activeRow.getCurrency("target_pay_calc")
                }).then(response => {
                this.setTotalAdvancesTooltip();
                this.setupBuyToMarket(this.activeRow);
            });
        }
    }

    setTotalAdvancesTooltip() {
        this.textboxTotalAdvances.tooltipDynamicContent = async () => {
            const layout = await Layout.getLoadedLayout("lme/dispatch/MovementAdvancesQuickInfo", { minWidth: 425, minHeight: 150});
            const response = await layout.mainDataSource.search({ movement_id: this.activeRow?.get("id") });
            if (response.modelRows.length > 0) {
                return layout;
            }
            else {
                return "No Advance History";
            }
        }

    }

    setSmsButtonImage(selectedRow: ModelRow, buttonSendSmsDriver: Button, driverNumberSuffix: String) {
        if (StringUtil.equalsIgnoreCase(selectedRow?.get("is_sms_opted_in"), "Y")) {
            buttonSendSmsDriver.imageName = ImageName.driverMsgNormal;
            if (driverNumberSuffix === "2") {
                this.driverTwoOptedOut = false;
            } else {
                this.driverOneOptedOut = false;
            }
        } else {
            buttonSendSmsDriver.imageName = ImageName.driverSmsOptedOut;
            if (driverNumberSuffix === "2") {
                this.driverTwoOptedOut = true;
            } else {
                this.driverOneOptedOut = true;
            }
        }
    }

    buttonSendSmsDriverOnClick(event: ClickEvent) {
        const carrierId = this.activeRow?.get("override_payee_id");
        const movementId = this.activeRow?.get("id");
        const orderId = this.activeRow?.get("orders.id");
        let driverName = this.activeRow?.get("override_driver_nm");
        let overrideDriverCell = this.activeRow?.get("override_drvr_cell");

        if (this.driverOneOptedOut) {
            if (!this.driverTwoOptedOut && this.activeRow?.get("override_driver2_nm") !== undefined) {
                driverName = this.activeRow?.get("override_driver2_nm");
                overrideDriverCell = this.activeRow?.get("override_drvr2_cell");
            }
        }

        if (movementId && carrierId) {
            SmsUtility.showSendSmsMessageSlideout(SmsUtility.setLayoutPropsViaFields(carrierId, movementId, orderId, driverName, overrideDriverCell));
        }
    }


    buttonSendSmsDriver2OnClick(event: ClickEvent) {
        const carrierId = this.activeRow?.get("override_payee_id");
        const movementId = this.activeRow?.get("id");
        const orderId = this.activeRow?.get("orders.id");
        let driverName = this.activeRow?.get("override_driver2_nm");
        let overrideDriverCell = this.activeRow?.get("override_drvr2_cell");

        if (this.driverTwoOptedOut) {
            if (!this.driverOneOptedOut && this.activeRow?.get("override_driver_nm") !== undefined) {
                driverName = this.activeRow?.get("override_driver_nm");
                overrideDriverCell = this.activeRow?.get("override_drvr_cell");
            }
        }
        if (movementId && carrierId) {
            SmsUtility.showSendSmsMessageSlideout(SmsUtility.setLayoutPropsViaFields(carrierId, movementId, orderId, driverName, overrideDriverCell));
        }
    }

    setCarrierEmptyCityState() {
        const cityId = this.sourceMovement.activeRow?.get("carrier_empty_from_city_id");
        if (cityId)
            Api.search("common/city", { id: cityId }).then(result => {
                const data = result?.data[0];
                this.cityStateCarrierEmptyFromCityId.textCombined.text = CityUtil.formatCityStateZip(data.name, data.state_id, data.zip_code);
                this.cityStateCarrierEmptyFromCityIdPickupDist();
            });
    }

    /** This is an event handler for the onClick event of buttonAssignCarrier.  */
    async buttonModifyPayOnClick(event: ClickEvent) {
        this.buttonModifyPay.busy = true;
        const isEdiCarrier: boolean = await this.checkCarrierForDataFusion(this.activeRow?.get("override_payee_id"));
        ModifyCarrierPay.showSlideout(
            this.activeRow?.get("id"),
            this.activeRow.get("payee.id"),
            (movementSaved: boolean) => this.sourceMovement.search({ id: this.activeRow?.get("id") }),
            undefined,
            isEdiCarrier
        ).then(() => this.buttonModifyPay.busy = false);
    }

    /** This is an event handler for the onBlur event of cityStateCarrierEmptyFromCityId.  */
    cityStateCarrierEmptyFromCityIdPickupDist() {
        const cityId = this.sourceMovement.activeRow?.get("carrier_empty_from_city_id") || this.cityStateCarrierEmptyFromCityId?.textCombined?.getFirstLookupModelData()?.get("id");
        const moveId = this.mainDataSource.activeRow?.get("id");
        if (!StringUtil.isEmptyString(cityId) && moveId != null) {
            Api.post("lme/dispatch/get-next-stop-distance", {
                city_id: cityId,
                movement_id: moveId,
                use_load_dist_profile: true
            }).then(response => {
                if (!StringUtil.isEmptyString(response?.data[0]?.distance)) {
                    this.labelMilesToPickup.text = `${response?.data[0]?.distance} Miles to Pick-up`;
                    this.labelNextStopDistance.text = `${response?.data[0]?.distance} ${response?.data[0]?.distance_um}`;
                } else {
                    this.labelMilesToPickup.text = "";
                    this.labelNextStopDistance.text = "";
                }
            });
        } else {
            this.labelMilesToPickup.text = "";
            this.labelNextStopDistance.text = "";
        }
    }

    /** This is an event handler for the onDataDisplay event of labelPayeeName.  */
    labelPayeeNameOnDataDisplay(event: DataDisplayEvent) {
        (event.target as Label).quickInfoOverrideKeyValue = this.mainDataSource.activeRow?.get("payee.id")
    }

    setTrackingRequiredTooltip(movementModelRow: ModelRow<any>, modelRow: ModelRow<any>) {
        let vendorDisplayName = "";
        try {
            Api.search("lme/dispatch/get-active-brokerage-tracking", {
                vendor_id: movementModelRow.get("required_vendor")
            }).then(response => {
                if (response !== null && response.data !== null
                    && response.data.length > 0 && response.data[0].vendor_name !== null) {
                    vendorDisplayName = response.data[0].vendor_name;
                    if (modelRow.get("tracking_badge_text", "") === "Tracking Automated") {
                        this.labelTrackingRequired.tooltipDynamicContent = {
                            text: "Tracking will be sent automatically to " + vendorDisplayName + ".",
                            align: HorizontalAlignment.CENTER
                        };
                    } else {
                        this.labelTrackingRequired.tooltipDynamicContent = new Textbox({
                            caption: "Vendor",
                            displayType: DisplayType.STRING,
                            printable: true,
                            text: vendorDisplayName
                        });
                    }
                } else {
                    this.tabTrackingRequirements.visible = false; // set to false because no vendor is active.
                }
            });
        } catch (error) {
            console.error(error);
        }
    }

    setCarrierStarRatingVisible(modelRow: ModelRow) {
        this.starratingRatingStarCount.visible = modelRow?.get("is_carrier_scorecard_licensed", false);
    }

    async checkCarrierForDataFusion(payeeId: String): Promise<boolean> {
        let result = "";
        await Api.search("lme/datafusion/df-carrier-search", {
            payee_id: payeeId
        }).then(response => {
            const data = response.data[0];
            result = data.is_datafusion_carrier;
        });
        return result != "false";
    }

    setCommissionableMarginTooltip() {
        CommonTooltips.setTooltipContentLayout(this.textboxCommissionableMargin, this.activeRow,
            "lme/dispatch/CommissionableMarginQuickInfo", { maxWidth: 265, minWidth: 265, minHeight: 100 });
        this.textboxCommissionableMargin.tooltipDynamicOptions = { position: Alignment.RIGHT };
    }
}
